/**
 * CRM Client Self-Service Tools - GST calculator, ABN lookup, depreciation calculator, etc.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calculator, Search, DollarSign, Percent, Building2, CheckCircle, XCircle, Loader2, ExternalLink,
} from 'lucide-react';

export function CRMToolsPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Business Tools</h1>
        <p className="text-sm text-muted-foreground mt-1">Quick calculators and lookup tools</p>
      </div>

      <Tabs defaultValue="gst" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="gst">GST</TabsTrigger>
          <TabsTrigger value="abn">ABN Lookup</TabsTrigger>
          <TabsTrigger value="payg">PAYG</TabsTrigger>
          <TabsTrigger value="depreciation">Depreciation</TabsTrigger>
        </TabsList>

        <TabsContent value="gst"><GSTCalculator /></TabsContent>
        <TabsContent value="abn"><ABNLookup /></TabsContent>
        <TabsContent value="payg"><PAYGCalculator /></TabsContent>
        <TabsContent value="depreciation"><DepreciationCalculator /></TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== GST Calculator ====================

function GSTCalculator() {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState<'add' | 'remove'>('add');

  const numAmount = parseFloat(amount) || 0;
  const gstAmount = mode === 'add' ? numAmount / 10 : numAmount / 11;
  const result = mode === 'add' ? numAmount + gstAmount : numAmount - gstAmount;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Percent className="h-4 w-4" /> GST Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Button variant={mode === 'add' ? 'default' : 'outline'} onClick={() => setMode('add')} className={mode === 'add' ? 'bg-[#5048e5]' : ''}>
            Add GST (10%)
          </Button>
          <Button variant={mode === 'remove' ? 'default' : 'outline'} onClick={() => setMode('remove')} className={mode === 'remove' ? 'bg-[#5048e5]' : ''}>
            Remove GST
          </Button>
        </div>
        <div>
          <Label>{mode === 'add' ? 'Amount (excl. GST)' : 'Amount (incl. GST)'}</Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={amount} onChange={e => setAmount(e.target.value)} type="number" step="0.01" placeholder="0.00" className="pl-9" />
          </div>
        </div>
        {numAmount > 0 && (
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount excl. GST</span>
              <span className="font-medium">${(mode === 'add' ? numAmount : result).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">GST (10%)</span>
              <span className="font-medium">${gstAmount.toFixed(2)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between text-sm">
              <span className="font-semibold">Amount incl. GST</span>
              <span className="font-bold text-lg">${(mode === 'add' ? result : numAmount).toFixed(2)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== ABN Lookup ====================

function ABNLookup() {
  const [abn, setAbn] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lookup = async () => {
    const cleaned = abn.replace(/\s/g, '');
    if (cleaned.length < 9) { setError('Enter a valid ABN (11 digits) or ACN (9 digits)'); return; }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const r = await fetch(`https://abr.business.gov.au/json/AbnDetails.aspx?abn=${cleaned}&callback=c&guid=`, { mode: 'no-cors' });
      // ABR requires a registered GUID, so we'll show the link instead
      setResult({ abn: cleaned, lookupUrl: `https://abr.business.gov.au/ABN/View?abn=${cleaned}` });
    } catch {
      setResult({ abn: cleaned, lookupUrl: `https://abr.business.gov.au/ABN/View?abn=${cleaned}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Search className="h-4 w-4" /> ABN Lookup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>ABN or ACN</Label>
          <div className="flex gap-2 mt-1">
            <Input value={abn} onChange={e => setAbn(e.target.value)} placeholder="e.g. 48 654 068 039" className="flex-1" />
            <Button onClick={lookup} disabled={loading} className="bg-[#5048e5] hover:bg-[#443fcc]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        {result && (
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <p className="text-sm">ABN: <span className="font-mono font-medium">{result.abn}</span></p>
            <Button variant="outline" size="sm" onClick={() => window.open(result.lookupUrl, '_blank')} className="text-xs">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> View on ABR
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== PAYG Calculator ====================

function PAYGCalculator() {
  const [gross, setGross] = useState('');
  const [period, setPeriod] = useState('weekly');

  const annualGross = parseFloat(gross) || 0;
  const annualized = period === 'weekly' ? annualGross * 52 : period === 'fortnightly' ? annualGross * 26 : period === 'monthly' ? annualGross * 12 : annualGross;

  // Simplified 2024-25 tax brackets for residents
  let tax = 0;
  if (annualized > 190000) tax = 51667 + (annualized - 190000) * 0.45;
  else if (annualized > 135000) tax = 29467 + (annualized - 135000) * 0.37;
  else if (annualized > 45000) tax = 5092 + (annualized - 45000) * 0.325;
  else if (annualized > 18200) tax = (annualized - 18200) * 0.19;

  const periodTax = period === 'weekly' ? tax / 52 : period === 'fortnightly' ? tax / 26 : period === 'monthly' ? tax / 12 : tax;
  const effectiveRate = annualized > 0 ? (tax / annualized) * 100 : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calculator className="h-4 w-4" /> PAYG Withholding Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Gross Pay</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={gross} onChange={e => setGross(e.target.value)} type="number" step="0.01" placeholder="0.00" className="pl-9" />
            </div>
          </div>
          <div>
            <Label>Pay Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="annually">Annually</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {annualized > 0 && (
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Annual gross salary</span>
              <span className="font-medium">${annualized.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tax per {period === 'annually' ? 'year' : period.replace('ly', '')}</span>
              <span className="font-medium text-red-600">${periodTax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Take-home per {period === 'annually' ? 'year' : period.replace('ly', '')}</span>
              <span className="font-bold text-green-600">${((parseFloat(gross) || 0) - periodTax).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm border-t pt-2">
              <span className="text-muted-foreground">Effective tax rate</span>
              <span className="font-medium">{effectiveRate.toFixed(1)}%</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Based on 2024-25 tax rates for Australian residents. Does not include Medicare levy.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== Depreciation Calculator ====================

function DepreciationCalculator() {
  const [cost, setCost] = useState('');
  const [life, setLife] = useState('5');
  const [method, setMethod] = useState<'prime' | 'diminishing'>('diminishing');

  const costNum = parseFloat(cost) || 0;
  const lifeNum = parseInt(life) || 5;

  const primeRate = 100 / lifeNum;
  const dimRate = primeRate * 2;
  const annualDeduction = method === 'prime' ? costNum * (primeRate / 100) : costNum * (dimRate / 100);

  // Check instant asset write-off eligibility (simplified)
  const isEligibleForWriteOff = costNum < 20000; // Simplified threshold

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calculator className="h-4 w-4" /> Depreciation Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Asset Cost</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={cost} onChange={e => setCost(e.target.value)} type="number" placeholder="0" className="pl-9" />
            </div>
          </div>
          <div>
            <Label>Effective Life (years)</Label>
            <Input value={life} onChange={e => setLife(e.target.value)} type="number" className="mt-1" />
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={(v: any) => setMethod(v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="diminishing">Diminishing Value</SelectItem>
                <SelectItem value="prime">Prime Cost</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {costNum > 0 && (
          <div className="space-y-3">
            {isEligibleForWriteOff && (
              <div className="rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/30 p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                    May qualify for instant asset write-off
                  </p>
                </div>
                <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                  Assets under $20,000 may be immediately deductible. Confirm eligibility with your accountant.
                </p>
              </div>
            )}

            <div className="rounded-lg bg-muted/50 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Method</span>
                <span className="font-medium">{method === 'prime' ? 'Prime Cost' : 'Diminishing Value'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Rate</span>
                <span className="font-medium">{(method === 'prime' ? primeRate : dimRate).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="font-semibold">Year 1 deduction</span>
                <span className="font-bold text-lg">${annualDeduction.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Assumes full year use. Part-year and pooling rules may apply.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
